import React from 'react';
import BlogRoll, { IBlogPost } from "./BlogRoll";
import Hero from "tbk-components/src/components/BlogRoll/BlogRollHero";
import {IBaseElementProps} from "tbk-components/src/components/BasicElement";
import cn from 'classnames';

/**
 * @usePostData
 * @post_type post
 */
export interface IBlogRollFeaturedPost extends IBlogPost {
}

/**
 * @useTermData
 * @taxonomy post_tag category
 */
export interface IBlogRollFilterTerms {
	term_id: number,
	name: string,
	taxonomy: string,
}

export interface IBlogRollWithFeaturedPostProps extends IBaseElementProps {
	topMargin?: 'standard' | 'xl' | 'small' | 'none';
	bottomMargin?: 'standard' | 'small' | 'none';
	hero?: IBlogRollFeaturedPost;
	postsPerRow?: 3 | 4;
	showFilterHeader?: boolean;
	showHeading?: boolean;
	/**
	 * The heading appears at the top of the widget
	 */
	heading?: string;
	/**
	 * Limit the posts to only those in the selected terms.
	 */
	terms?: IBlogRollFilterTerms[],
	/**
	 * A button to view other posts
	 */
	showSeeMore?: boolean;
	seeMoreLabel?: string;
	/**
	 * The URL the "See More" button will go to
	 */
	archivePage?: string;
	showPagination?: boolean;
	showExcerpt?: boolean;
	/**
	 * Show the "Read on" button on the tile
	 */
	showButton?: boolean;
	/**
	 * Show the author byline on the tile
	 */
	showByline?: boolean;
	/**
	 * True to show only the filter bar in this widget
	 */
	filterBarOnly?: boolean;
	/**
	 * The URL of the page the filter bar directs to
	 */
	filterBarTargetUrl?: string;
	/**
	 * The message to show if no results found
	 */
	noResultsMessage?: string;
	/**
	 * @noUI
	 */
	fetchArgs?:any;
	/**
	 * @noUI
	 */
	api_map?:any;
	/**
	 * @noUI
	 */
	assets?:any;
}

/**
 * Show the BlogRoll widget, with ability to pin a featured post
 *
 * @block
 * @requiresHooks
 * @icon id-alt
 */
const BlogRollWithFeaturedPost: React.FC<IBlogRollWithFeaturedPostProps> =
({
	className,
	classNames = [],
	topMargin = 'standard',
	bottomMargin = 'standard',
	hero,
	heading,
	archivePage,
	terms,
	postsPerRow = 3,
	showFilterHeader = true,
	showHeading = false,
	showSeeMore = false,
	seeMoreLabel = 'Read On',
	showPagination = true,
	showExcerpt = true,
	showButton = true,
	showByline= true,
	filterBarOnly = false,
	filterBarTargetUrl = '',
	noResultsMessage = 'No results found.  Please adjust your filters',
	fetchArgs,
	api_map,
	assets,
}) => {
	const adjustedFetchArgs = { ...( fetchArgs || {} ) }; // Initialize it by cloning fetchArgs or empty object

	if ( hero ) {
		adjustedFetchArgs.post__not_in = [ hero['ID'] ]; // written like this gets around IBlogRollFeaturedPost not having an ID
	}

	if ( terms ) {
		terms.forEach( term => {
			if ( ! adjustedFetchArgs[ term.taxonomy ] ) {
				adjustedFetchArgs[ term.taxonomy ] = [];
			}
			adjustedFetchArgs[ term.taxonomy ].push( term.term_id );
		});
	}

	return 	<div className={className || cn('blogroll-page', ...classNames, topMargin && `margin-top--${topMargin}`, bottomMargin && `margin-bottom--${bottomMargin}`)}>
		{heading && <h1>{heading}</h1>}

		{hero && <Hero post={hero} readMoreLabel={seeMoreLabel} />}

		<div className="blogroll__inner">
			<BlogRoll
				heading={heading}
				archivePage={archivePage}
				postsPerRow={postsPerRow}
				showFilterHeader={showFilterHeader}
				showHeading={showHeading}
				showSeeMore={showSeeMore}
				seeMoreLabel={seeMoreLabel}
				showPagination={showPagination}
				showExcerpt={showExcerpt}
				showButton={showButton}
				showByline={showByline}
				filterBarOnly={filterBarOnly}
				filterBarTargetUrl={filterBarTargetUrl}
				fetchArgs={{ ...adjustedFetchArgs, posts_per_page: 12 }}
				api_map={api_map}
				assets={assets}
				noResultsMessage={noResultsMessage} context={undefined}
			/>
		</div>
	</div>
}

export default BlogRollWithFeaturedPost;
